/**
 * Diagnostic logging singleton — batches events and POSTs to /api/diagnostics/log.
 * Fire-and-forget: never throws, never awaited by callers.
 */

import { CLOUD_API_URL } from '../config/serverConfig';

const LS_SESSION_ID = 'maestro_diagnostic_session_id';
const LS_ACTIVE = 'maestro_diagnostic_active';

export type DiagnosticLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error';
export type DiagnosticCategory =
  | 'discovery'
  | 'transport'
  | 'volume'
  | 'playback'
  | 'protocol'
  | 'capability'
  | 'other';

export interface DiagnosticEventInput {
  level: DiagnosticLevel;
  category: DiagnosticCategory;
  message: string;
  payload?: Record<string, unknown>;
  device_fingerprint?: string;
}

function getAuthTokenSync(): string | null {
  try {
    const stored = localStorage.getItem('maestro_auth');
    if (stored) return JSON.parse(stored).accessToken || null;
  } catch {
    /* ignore */
  }
  return null;
}

class DiagnosticLogger {
  private buffer: DiagnosticEventInput[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private _hasTauri: boolean | null = null;

  private get hasTauri(): boolean {
    if (this._hasTauri === null) {
      this._hasTauri =
        typeof window !== 'undefined' && '__TAURI__' in window;
    }
    return this._hasTauri;
  }

  get isActive(): boolean {
    try {
      return (
        localStorage.getItem(LS_ACTIVE) === 'true' &&
        !!localStorage.getItem(LS_SESSION_ID)
      );
    } catch {
      return false;
    }
  }

  /** Object-style API */
  log(event: DiagnosticEventInput): void {
    try {
      if (this.hasTauri) {
        try {
          (window as any).__TAURI__.core
            .invoke('diagnostic_log_js', {
              level: event.level,
              category: event.category,
              message: event.message,
              data: event.payload || null,
            })
            .catch(() => {});
        } catch {
          /* silent */
        }
      }

      if (!this.isActive) return;
      this.buffer.push(event);
      if (!this.flushTimer) {
        this.flushTimer = setInterval(() => void this.flush(), 3000);
      }
    } catch {
      /* never propagate */
    }
  }

  /** Flush buffered events (single POST per event — API accepts one event per request). */
  private async flush(): Promise<void> {
    if (this.buffer.length === 0) {
      if (this.flushTimer) {
        clearInterval(this.flushTimer);
        this.flushTimer = null;
      }
      return;
    }

    const events = this.buffer.splice(0, this.buffer.length);
    const sessionId = (() => {
      try {
        return localStorage.getItem(LS_SESSION_ID);
      } catch {
        return null;
      }
    })();
    if (!sessionId) {
      if (this.flushTimer) {
        clearInterval(this.flushTimer);
        this.flushTimer = null;
      }
      return;
    }

    const token = getAuthTokenSync();
    if (!token) return;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    try {
      await Promise.all(
        events.map((e) =>
          fetch(`${CLOUD_API_URL}/api/diagnostics/log`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              session_id: sessionId,
              level: e.level,
              category: e.category,
              message: e.message,
              payload: e.payload ?? null,
              device_fingerprint: e.device_fingerprint ?? undefined,
            }),
          }).catch(() => {}),
        ),
      );
    } catch {
      /* swallow */
    }

    if (this.buffer.length === 0 && this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /** Stop timer and attempt a final flush (non-blocking). */
  stop(): void {
    try {
      if (this.flushTimer) {
        clearInterval(this.flushTimer);
        this.flushTimer = null;
      }
      void this.flush();
    } catch {
      /* ignore */
    }
  }
}

export const diagnosticLogger = new DiagnosticLogger();

/** Legacy functional API — (level, category, message, payload?, deviceFingerprint?) */
export function diagnosticLog(
  level: string,
  category: string,
  message: string,
  payload?: Record<string, unknown>,
  deviceFingerprint?: string,
): void {
  diagnosticLogger.log({
    level: level as DiagnosticLevel,
    category: category as DiagnosticCategory,
    message,
    payload,
    device_fingerprint: deviceFingerprint,
  });
}

export function isDiagnosticActive(): boolean {
  try {
    return (
      localStorage.getItem(LS_ACTIVE) === 'true' &&
      !!localStorage.getItem(LS_SESSION_ID)
    );
  } catch {
    return false;
  }
}

export function setDiagnosticSession(sessionId: string | null, active: boolean): void {
  try {
    if (sessionId) {
      localStorage.setItem(LS_SESSION_ID, sessionId);
    } else {
      localStorage.removeItem(LS_SESSION_ID);
    }
    localStorage.setItem(LS_ACTIVE, active ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

export function clearDiagnosticSession(): void {
  diagnosticLogger.stop();
  try {
    localStorage.removeItem(LS_SESSION_ID);
    localStorage.removeItem(LS_ACTIVE);
    localStorage.removeItem('maestro_diagnostic_ticket_number');
  } catch {
    /* ignore */
  }
}
